import React from "react";
import { Link } from "react-router-dom";

/**
 * Interface representing metadata for a single downloadable client application build.
 * This structure helps keep the UI rendering code clean and extensible.
 */
interface DownloadOption {
  id: string; // Machine-readable platform identifier (e.g., "windows")
  platformName: string; // Human-readable platform name (e.g., "Windows")
  architecture: string; // CPU architectures supported (e.g., "Intel/AMD 64-bit")
  version: string; // Version tag (e.g., "v1.0.0-beta")
  fileSize: string; // Size of the binary file for user information (e.g., "12.5 MB")
  releaseDate: string; // Date this specific release was packaged
  downloadUrl: string; // Placeholder URL pointing to the binary asset
  alternativeDownloadUrl?: string; // Optional alternate format (e.g., AppImage for Linux)
  alternativeUrlLabel?: string; // Label for the alternative download button
  checksum: string; // SHA-256 hash to allow users to verify file integrity
  systemRequirements: string; // Minimum OS version needed to run the client
  iconEmoji: string; // OS symbol to display on the card header
}

/**
 * DownloadPage Component
 *
 * This page provides public download options for the MultiKeys desktop client.
 * It features cards for Windows, macOS, and Linux with clear platform identifiers,
 * installer file sizes, checksums, system requirements, and setup links.
 *
 * Theme details:
 * - Employs a dark background (`var(--color-bg-primary)`) and light text (`var(--color-text-primary)`).
 * - Focus states and card borders use the MultiKeys neon cyan accent (`var(--color-accent)`: `#00D4FF`).
 * - WCAG Contrast compliance is maintained by ensuring interactive elements use dark text
 *   on the neon cyan background when hovered/active.
 *
 * Non-coders note:
 * - To update version numbers, file sizes, or download URLs, modify the objects inside the
 *   `DOWNLOADS_DATA` array.
 */
export const DownloadPage: React.FC = () => {
  // --- DOWNLOAD DETAILS DATABASE ---
  // Modify these constants to update the client binaries list:
  const DOWNLOADS_DATA: DownloadOption[] = [
    {
      id: "windows",
      platformName: "Windows",
      architecture: "Intel/AMD (64-bit)",
      version: "v1.0.0-beta",
      fileSize: "12.5 MB",
      releaseDate: "2026-06-15",
      downloadUrl: "https://multikeys.dev/downloads/multikeys-windows.exe",
      checksum: "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
      systemRequirements: "Windows 10 or 11",
      iconEmoji: "🪟",
    },
    {
      id: "macos",
      platformName: "macOS",
      architecture: "Universal (M1/M2/M3 & Intel)",
      version: "v1.0.0-beta",
      fileSize: "15.2 MB",
      releaseDate: "2026-06-15",
      downloadUrl: "https://multikeys.dev/downloads/multikeys-macos.dmg",
      checksum: "7d558a2d1e2e718224dc4d3e2efd978a6ff67140411cfb69e9e62f5509939e08",
      systemRequirements: "macOS 11 Big Sur or newer",
      iconEmoji: "🍎",
    },
    {
      id: "linux",
      platformName: "Linux",
      architecture: "x86_64 (64-bit)",
      version: "v1.0.0-beta",
      fileSize: "18.1 MB",
      releaseDate: "2026-06-15",
      downloadUrl: "https://multikeys.dev/downloads/multikeys-linux.deb",
      alternativeDownloadUrl: "https://multikeys.dev/downloads/multikeys-linux.AppImage",
      alternativeUrlLabel: "Download AppImage",
      checksum: "3c968a3d123d91cf0ee71c149afbf4c8996fb92427ae41e4649b934ca495991b",
      systemRequirements: "Ubuntu 20.04+, Debian 11+, or Fedora 36+",
      iconEmoji: "🐧",
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "var(--color-bg-primary)",
        color: "var(--color-text-primary)",
        minHeight: "75vh",
        padding: "4rem 2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      data-testid="download-page"
    >
      {/* 1. Header Section */}
      <div style={{ textAlign: "center", marginBottom: "4rem", maxWidth: "700px" }}>
        <h1
          style={{
            fontSize: "2.75rem",
            fontWeight: 800,
            lineHeight: 1.2,
            marginBottom: "1rem",
          }}
        >
          Download <span style={{ color: "var(--color-accent)" }}>MultiKeys</span> Client
        </h1>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "1.15rem" }}>
          Get the desktop client to connect and share your keyboard and mouse controls. Make sure to pair each machine on your dashboard after installation.
        </p>
      </div>

      {/* 2. Download Platform Cards Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "2rem",
          width: "100%",
          maxWidth: "1100px",
          marginBottom: "4rem",
        }}
        data-testid="download-options-grid"
      >
        {DOWNLOADS_DATA.map((option) => (
          <div
            key={option.id}
            data-testid={`download-card-${option.id}`}
            style={{
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--border-radius-lg)",
              padding: "2.25rem",
              display: "flex",
              flexDirection: "column",
              transition: "transform var(--transition-speed) ease, border-color var(--transition-speed) ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "var(--color-accent)";
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "var(--color-border)";
              e.currentTarget.style.transform = "none";
            }}
          >
            {/* Platform Icon & Name */}
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem" }}>
              <span style={{ fontSize: "2.25rem" }} role="img" aria-label={`${option.platformName} icon`}>
                {option.iconEmoji}
              </span>
              <div>
                <h2 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "0.25rem" }}>
                  {option.platformName}
                </h2>
                <span style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", fontWeight: 500 }}>
                  {option.architecture}
                </span>
              </div>
            </div>

            {/* Release Meta info */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                fontSize: "0.9rem",
                color: "var(--color-text-secondary)",
                marginBottom: "2rem",
                padding: "1rem",
                backgroundColor: "var(--color-bg-primary)",
                borderRadius: "var(--border-radius-md)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Version:</span>
                <strong style={{ color: "var(--color-text-primary)" }}>{option.version}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>File Size:</span>
                <span>{option.fileSize}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Requirements:</span>
                <span>{option.systemRequirements}</span>
              </div>
            </div>

            {/* Primary Action Button */}
            <a
              href={option.downloadUrl}
              data-testid={`download-button-${option.id}`}
              style={{
                display: "block",
                textAlign: "center",
                backgroundColor: "var(--color-accent)",
                color: "var(--color-bg-primary)", // Correct color contrast (dark text on cyan background)
                fontWeight: 700,
                padding: "0.85rem 1.5rem",
                borderRadius: "var(--border-radius-md)",
                textDecoration: "none",
                marginBottom: option.alternativeDownloadUrl ? "0.75rem" : "0px",
                transition: "background-color var(--transition-speed) ease",
                boxShadow: "0 0 10px rgba(0, 212, 255, 0.15)",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-accent-hover)";
                e.currentTarget.style.boxShadow = "0 0 15px var(--color-accent)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-accent)";
                e.currentTarget.style.boxShadow = "0 0 10px rgba(0, 212, 255, 0.15)";
              }}
            >
              Download for {option.platformName}
            </a>

            {/* Optional Alternative Format (Linux AppImage) */}
            {option.alternativeDownloadUrl && (
              <a
                href={option.alternativeDownloadUrl}
                data-testid={`download-alternative-${option.id}`}
                style={{
                  display: "block",
                  textAlign: "center",
                  backgroundColor: "var(--color-bg-primary)",
                  color: "var(--color-text-primary)",
                  border: "1px solid var(--color-border)",
                  fontWeight: 600,
                  padding: "0.75rem 1.5rem",
                  borderRadius: "var(--border-radius-md)",
                  textDecoration: "none",
                  transition: "all var(--transition-speed) ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-accent)";
                  e.currentTarget.style.color = "var(--color-accent)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-border)";
                  e.currentTarget.style.color = "var(--color-text-primary)";
                }}
              >
                {option.alternativeUrlLabel || "Download Alternate format"}
              </a>
            )}

            {/* Cryptographic SHA-256 Checksum for security audits */}
            <div style={{ marginTop: "auto", paddingTop: "1.5rem" }}>
              <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", display: "block" }}>
                SHA-256 Checksum:
              </span>
              <code
                data-testid={`checksum-${option.id}`}
                style={{
                  fontSize: "0.7rem",
                  color: "var(--color-text-muted)",
                  wordBreak: "break-all",
                  fontFamily: "var(--font-mono)",
                  display: "block",
                  marginTop: "0.25rem",
                }}
              >
                {option.checksum}
              </code>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Security Warning and Assistance Banner */}
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          backgroundColor: "var(--color-bg-secondary)",
          border: "1px dashed var(--color-border)",
          borderRadius: "var(--border-radius-lg)",
          padding: "2rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
        data-testid="download-support-banner"
      >
        <h3 style={{ fontSize: "1.2rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "0.5rem" }}>
          🛡️ Secure & Verified Installation
        </h3>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", lineHeight: 1.6 }}>
          All MultiKeys desktop client packages are cryptographically signed. During the beta feasibility test, Windows users may encounter a SmartScreen warning because our developer certificate is young. If this happens, click <strong>"More Info"</strong> and select <strong>"Run Anyway"</strong> to complete setup.
        </p>
        <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "1rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
          <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            Need step-by-step instructions?
          </span>
          <Link
            to="/docs"
            style={{
              color: "var(--color-accent)",
              fontWeight: 600,
              fontSize: "0.9rem",
              textDecoration: "underline",
            }}
            data-testid="link-to-setup-guide"
          >
            View Setup & Installation Guide →
          </Link>
        </div>
      </div>
    </div>
  );
};
